import type { Translation } from './translations.es'

export const en: Translation = {
  language: {
    label: 'Language',
    es: 'ES',
    en: 'EN',
    switchTo: 'Cambiar a Español',
  },
  nav: {
    services: 'Services',
    portfolio: 'Portfolio',
    about: 'About Us',
    reviews: 'Reviews',
    cta: 'GET STARTED →',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },
  hero: {
    imageAlt: 'Professional contractor website shown on a laptop and phone',
    titleA: 'Make Your Business Look',
    titleHighlight: 'As Professional As Your Work.',
    titleSub: 'A Website That Brings You Customers.',
    subtitleBefore: 'See your website before you pay in full. Your demo for just',
    subtitlePrice: '$25',
    subtitleAfter: '— if you like it, you pay the rest. If not, that is it.',
    ctaWhatsapp: 'MESSAGE US ON WHATSAPP →',
    ctaWork: 'See Our Work',
    badges: [
      'Premium Design',
      'Ready in 72 Hours',
      'Satisfaction Guaranteed',
      'Demo For Just $25',
    ],
  },
  socialProof: {
    label: 'WE WORK WITH CONTRACTORS IN:',
    types: [
      'Roofing',
      'Landscaping',
      'Tree Service',
      'Construction',
      'Pressure Washing',
      'Junk Removal',
      'HVAC',
      'Plumbing',
    ],
  },
  problem: {
    titleA: 'You Do Incredible Work.',
    titleHighlight: 'But Your Online Presence Is Costing You Customers.',
    p1a: 'Before they call you, customers look you up on Google. They judge your professionalism in under',
    p1strong: '7 seconds',
    p1b: '.',
    p2a: 'And if what they see does not reflect the quality of your work,',
    p2strong: 'they call someone else.',
    p3: 'Not because they do better work than you. Just because they look more professional online.',
    imageAlt:
      'Comparison of an outdated contractor website versus a professional one',
    before: 'Before',
    after: 'After',
    painPoints: [
      {
        problem: 'Outdated design',
        cost: 'Zero initial trust',
      },
      {
        problem: 'Generic template',
        cost: 'You look amateur',
      },
      {
        problem: 'Not showing on Google',
        cost: 'Competition wins',
      },
    ],
    closing1:
      'A homeowner with a $10,000 project just hired your competition.',
    closing2: 'Because their website looked more trustworthy.',
    closing3: 'That ends today.',
  },
  mechanism: {
    badge: 'The Solution',
    title: 'Introducing The Professional Presence System',
    subtitle:
      'The complete digital solution built exclusively for local contractors who want to look as professional as they work — for one affordable payment, with no hassle.',
    colFeature: 'Feature',
    colOther: 'Other Options',
    colUs: 'AS Elite Contractor',
    comparisons: [
      {
        feature: 'Focus',
        other: 'Generic templates for any business',
        us: 'Built exclusively for contractors',
      },
      {
        feature: 'Strategy',
        other: 'Design only',
        us: 'Design + conversion strategy',
      },
      {
        feature: 'Risk',
        other: 'You pay everything upfront without seeing anything',
        us: '$25 demo — you pay the rest only if you like it',
      },
      {
        feature: 'Time',
        other: 'Weeks or months to launch',
        us: 'Ready in under 72 hours',
      },
      {
        feature: 'Visibility',
        other: 'No SEO strategy',
        us: 'Local SEO included from day one',
      },
      {
        feature: 'Support',
        other: 'No support after launch',
        us: 'Optional maintenance available',
      },
      {
        feature: 'Results',
        other: 'Looks good but does not convert',
        us: 'Built to turn visits into calls',
      },
    ],
  },
  portfolio: {
    badge: 'Our Work',
    title: 'Websites We Have',
    titleHighlight: 'Built',
    subtitle:
      'Premium designs that turn visitors into customers — ready in under 72 hours.',
    cta: 'I Want Mine →',
    websiteAltPrefix: 'Website for',
    trades: {
      junkRemoval: 'Junk Removal',
      construction: 'Construction',
      landscaping: 'Landscaping',
      treeService: 'Tree Service',
      irrigation: 'Irrigation',
    },
  },
  google: {
    badge: 'Separate Service · Optional',
    title: 'Management of Your',
    titleHighlight: 'Google Business Profile',
    descA: 'This service is',
    descIndependent: 'separate from the website',
    descB: 'and has an extra cost of',
    descPrice: '$30/month',
    descC:
      '. We take care of managing it so you show up first in your area and get more customers — without paying for ads.',
    noteLabel: 'Note:',
    noteText:
      'the profile is created by the client. We only manage and optimize it.',
    included: [
      'We upload photos of your work',
      'We reply to your reviews',
      'Optimization to rank first',
    ],
    optimized: 'Optimized',
    profileAltPrefix: 'Google profile for',
    actions: ['Directions', 'Save', 'Call', 'Website'],
  },
  pricing: {
    title: 'Everything You Need.',
    titleHighlight: 'One Single Payment.',
    badge: 'Main Offer',
    imageAlt: 'Example of a professional contractor website shown on a laptop',
    cardTitle: 'YOUR WEBSITE DEMO',
    price: '$25',
    priceUnit: 'for your demo',
    noteA: 'If you like it, you pay the',
    noteRest: 'remaining $225',
    noteB: 'and we launch it ($250 total). If you do not like it,',
    noteBold: 'you are only charged for the demo',
    noteC: '. And honestly, we are confident you are going to love it.',
    features: [
      'Start with just $25 — no risk',
      'See your real website before paying more',
      'If you do not like it, you pay nothing else',
      'No contracts or monthly fees',
    ],
    cta: 'I WANT MY $25 DEMO',
    paymentTitle: 'Secure payment methods',
    zelle: 'Zelle',
    card: 'Card',
    viaStripe: 'via Stripe',
    domainNote:
      'The domain is included for 1 year. After the first year it renews separately with an annual cost.',
    featureCards: [
      {
        title: 'Premium Design',
        desc: 'Mobile-first and optimized',
      },
      {
        title: 'Credibility',
        desc: 'Reviews and project gallery',
      },
      {
        title: 'Growth',
        desc: 'Local SEO and Google Business',
      },
    ],
    optional: 'Optional',
    maintenance: 'Maintenance',
    maintenanceDesc: 'Updates and support',
    perMonth: '/ month',
    otherTitle: 'Have a different type of business?',
    otherDesc:
      'We also build websites for any business. Ask us, no strings attached.',
    otherCta: 'Ask Us',
  },
  process: {
    title: 'Ready In Under 72 Hours.',
    titleHighlight: 'That Simple.',
    steps: [
      {
        title: 'Contact',
        desc: 'Message us on WhatsApp',
      },
      {
        title: 'Information',
        desc: 'Send us your details and photos',
      },
      {
        title: 'Demo',
        desc: 'Just $25 to get started',
      },
      {
        title: 'Build',
        desc: 'We design your website',
      },
      {
        title: 'You Decide',
        desc: 'You like it, you pay the rest',
      },
      {
        title: 'Launch',
        desc: 'Your site goes live',
      },
    ],
  },
  story: {
    title: 'We Built This For Contractors Who',
    titleHighlight: 'Deserve More',
    p1: 'We watched excellent contractors lose jobs to mediocre competitors simply because their digital presence looked amateur.',
    p2a: 'Many thought a professional website cost thousands of dollars and months of waiting.',
    p2strong: 'It does not have to be that way.',
    missionTitle: 'Our Mission',
    missionQuote:
      'Give contractors the premium online presence they deserve — at an affordable price, in record time, with no hassle.',
    bullets: [
      'Your website demo for just $25',
      'Ready in under 72 hours',
      'No mandatory monthly fees',
      'No long contracts or surprises',
    ],
  },
  testimonials: {
    title: 'The Contractors Who Took The Step',
    titleHighlight: 'Have No Regrets',
    reviewCount: '· 120+ reviews',
    verified: 'Verified AS Elite Contractor client',
    prevLabel: 'Previous review',
    nextLabel: 'Next review',
    dotLabelPrefix: 'Go to review',
    items: [
      {
        quote:
          'Honestly I was not expecting much, but in less than 3 days my page was live. The first week I got 4 calls from Google. For $250 it was the best decision I made for my business.',
        author: 'Marcus Thompson',
        role: 'Tree Service',
        location: 'Austin, TX',
        timeAgo: '2 weeks ago',
      },
      {
        quote:
          'My work was always good but I was embarrassed to show my old page. Now customers come in with more confidence and they even push back less on price. Very happy with the result.',
        author: 'Roberto Mendoza',
        role: 'Roofing & Repairs',
        location: 'Houston, TX',
        timeAgo: '1 month ago',
      },
      {
        quote:
          'I thought a website like this would cost thousands. I paid $250 and in 72 hours I had something that looks better than my competitor who took months. Communication over WhatsApp was lightning fast.',
        author: 'Carlos Díaz',
        role: 'General Contractor',
        location: 'Phoenix, AZ',
        timeAgo: '3 weeks ago',
      },
      {
        quote:
          'The $30 a month plan is worth every penny. Every time I finish a new project I send them the photos and they upload them the same day. First-class service, I recommend them 100%.',
        author: 'James Rivera',
        role: 'Landscaping',
        location: 'Dallas, TX',
        timeAgo: '5 days ago',
      },
      {
        quote:
          'What surprised me most was that my own customers loved the page. Several told me "your business looks so professional". That gave me a lot more credibility to close big jobs.',
        author: 'Andrés Castillo',
        role: 'Pressure Washing',
        location: 'Orlando, FL',
        timeAgo: '1 week ago',
      },
      {
        quote:
          'We put the page QR code on our trucks and now it is free advertising rolling all over the city. People scan the code at red lights and message us straight on WhatsApp. Awesome.',
        author: 'Luis Hernández',
        role: 'HVAC & Plumbing',
        location: 'San Antonio, TX',
        timeAgo: '2 weeks ago',
      },
      {
        quote:
          'In the first week I already had people asking for quotes through the page. I used to hand out flyers and now customers find me on their own. Worth every dollar of the $250.',
        author: 'Miguel Ángel Soto',
        role: 'Construction',
        location: 'Las Vegas, NV',
        timeAgo: '4 days ago',
      },
    ],
    stats: [
      {
        data: '97%',
        desc: 'of consumers search online before hiring',
        meaning: 'Your customers are searching for you right now',
      },
      {
        data: '75%',
        desc: 'never go past the first page of Google',
        meaning: 'If you are not there, you do not exist',
      },
      {
        data: '88%',
        desc: 'of mobile searchers make contact within 24 hours',
        meaning: 'Mobile presence = immediate customers',
      },
      {
        data: '3x',
        desc: 'more trust is generated by professional websites',
        meaning: 'Look premium, get premium customers',
      },
    ],
  },
  guarantee: {
    title: 'Zero Risk. 100% Satisfaction.',
    titleHighlight: 'Or We Fix It.',
    p1: 'We know you have probably been let down before.',
    p2: 'Maybe someone built you a website that looked nothing like what you wanted. Maybe you paid and never saw results.',
    p3: 'That is not how we operate.',
    confidenceBadge: 'We stand behind our work',
    confidenceA: 'We are so confident in the quality of what we do that',
    confidenceHighlight: 'we know you are going to love your page',
    confidenceB:
      '. That is why we let you see it before paying in full — we do not need to convince you with words, we show you with your own website.',
    guaranteeTitle: 'Our Guarantee:',
    items: [
      {
        promise: 'Just $25 for your demo',
        meaning: 'That is all you risk to see your real website',
      },
      {
        promise: 'If you do not like it, it ends there',
        meaning: 'You are only charged for the demo, no strings attached',
      },
      {
        promise: 'Revisions included',
        meaning: 'We do not stop until you love it',
      },
      {
        promise: 'Transparent process',
        meaning: 'You know exactly what is happening at every stage',
      },
      {
        promise: 'Direct communication on WhatsApp',
        meaning: 'Fast answers, no middlemen',
      },
      {
        promise: 'No forced monthly fees',
        meaning: 'You pay for maintenance only if you want it',
      },
    ],
    closingA: 'You pay the rest only if you',
    closingHighlight: 'love',
    closingB: ' your demo. If not, you only paid the $25.',
  },
  urgency: {
    badge: 'Limited spots this week',
    title: 'Every Day Without A Professional Website Is Money Left On The Table',
    p1: 'Right now, a homeowner in your area just searched for your service on Google and picked the option that looked most professional.',
    p2: 'Was it you?',
    p3: 'If not — that job could have been yours.',
    offerA: 'For just',
    offerPrice: '$25 today',
    offerB: 'you can see your website before deciding.',
    clock: 'Your website working in under 72 hours.',
    cta: 'Save My Spot →',
    importantLabel: 'Important:',
    importantText:
      'we work with a limited number of clients per week to guarantee full attention and premium quality on every website.',
  },
  finalCta: {
    titleA: 'Your Business Does Premium Work.',
    titleHighlight: 'It Is Time Your Website Showed It.',
    subtitle:
      'Start today with just $25 and see your real demo in under 72 hours. If you like it, you pay the rest. If not, you are only charged for the demo. No contracts or mandatory monthly fees.',
    cardTitle: 'CONTACT US ON WHATSAPP NOW',
    cardSub: 'Send us "WEBSITE" and we reply today',
    checks: [
      'Fast response',
      'No pressure, no commitment',
      'We explain everything in minutes',
      'We start when you are ready',
    ],
    cta: 'MESSAGE US ON WHATSAPP NOW →',
    summaryTitle: 'QUICK OFFER SUMMARY',
    rows: [
      {
        label: 'To get started only',
        value: '$25 (your demo)',
      },
      {
        label: 'If you like it, you pay',
        value: 'Remaining $225',
      },
      {
        label: 'If you do not like it',
        value: 'You only pay the demo',
      },
      {
        label: 'Complete professional website',
        value: '$250 total',
      },
      {
        label: 'Ready in',
        value: 'Under 72 hours',
      },
      {
        label: 'Mandatory monthly fees',
        value: '$0 — None',
      },
      {
        label: 'Optional maintenance',
        value: '$30/month if you want it',
      },
      {
        label: 'Long contract',
        value: 'Does not exist',
      },
      {
        label: 'Price surprises',
        value: 'None',
      },
    ],
  },
  faq: {
    title: 'Got Questions?',
    titleHighlight: 'We Have Answers.',
    items: [
      {
        q: 'How does the $25 demo work?',
        a: 'You pay just $25 and we build a real demo of your website with your information, your photos and your services. If you like it, you pay the remaining $225 and we launch it. If you do not like it, it ends there — you were only charged for the demo.',
      },
      {
        q: 'How much does the complete website cost?',
        a: '$250 total. You start with $25 for the demo and the remaining $225 is only paid if you decide to launch it. No mandatory monthly fees and no hidden costs.',
      },
      {
        q: 'Does the $25 demo count toward the total?',
        a: 'Yes. The $25 is part of the $250 — that is why the rest is only $225 when you decide to move forward.',
      },
      {
        q: 'Why is it so cheap?',
        a: 'Because we specialize only in contractors and have a very efficient process: we use a proven structure that we adapt to your business, with no expensive agencies in the middle and no months of work. We prefer to charge affordably and win on volume and referrals instead of inflating the price. Same professional result, without the cost of a traditional agency.',
      },
      {
        q: 'How much do I need to get started?',
        a: 'Just $25. With that we save your spot and build the demo of your website.',
      },
      {
        q: 'When do I pay the rest?',
        a: 'You pay the remaining $225 only when you see your finished demo and decide you want it. Never before.',
      },
      {
        q: 'Is the domain included?',
        a: 'Yes, the domain is included for 1 year. When that year ends it renews separately with an annual cost, and we let you know well before it expires.',
      },
      {
        q: 'Are there monthly fees?',
        a: 'There are no mandatory monthly fees. We offer an optional maintenance plan for $30/month if you want it.',
      },
      {
        q: 'What does the $30 maintenance include?',
        a: 'Photo updates, content changes, technical support and direct communication on WhatsApp.',
      },
      {
        q: 'How long does it take?',
        a: 'Under 72 hours once we have all the information about your business.',
      },
      {
        q: 'What information do you need?',
        a: 'Business name, services, photos, contact number, service area and any important details.',
      },
      {
        q: 'How do I contact you?',
        a: 'On WhatsApp at +44 7311 754270 — send us "WEBSITE" and we will take care of you today.',
      },
      {
        q: 'Do you work with all contractors?',
        a: 'Yes — roofing, landscaping, tree service, construction, pressure washing, HVAC, plumbing, junk removal and more.',
      },
      {
        q: 'What if I do not like the design?',
        a: 'First we make revisions until it looks the way you want. And if it still does not convince you, you simply do not pay the rest — you were only charged the $25 demo.',
      },
      {
        q: 'Do I need to know anything technical?',
        a: 'Absolutely not. We handle everything. You just give us the information and approve the result.',
      },
    ],
  },
  footer: {
    logoAlt: 'AS Elite Contractor Logo',
    tagline: 'Built For Serious Contractors',
    services: 'Services',
    portfolio: 'Portfolio',
    about: 'About Us',
    reviews: 'Reviews',
    contact: 'Contact',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    whatsapp: 'WhatsApp:',
    email: 'Email:',
    tiktok: 'TikTok:',
    copyright: '© 2024 AS Elite Contractor. All Rights Reserved.',
  },
  legal: {
    close: 'Close',
    lastUpdated: 'Last updated: 2024 · AS Elite Contractor',
    privacy: {
      title: 'Privacy Policy',
      sections: [
        {
          heading: '1. Information we collect',
          body: 'We collect the information you provide directly when contacting us on WhatsApp or through forms, such as your name, phone number, business name, services you offer and any material (photos, text) needed to build your website.',
        },
        {
          heading: '2. How we use your information',
          body: 'We use your information solely to create, deliver and maintain your website, communicate with you about your project and, if you request it, manage your Google Business profile. We do not sell or rent your information to third parties.',
        },
        {
          heading: '3. Sharing information',
          body: 'We only share data with providers strictly necessary to operate your site (for example, hosting or domain services). These providers are required to protect your information.',
        },
        {
          heading: '4. Security',
          body: 'We take reasonable measures to protect your information. However, no method of transmission over the internet is 100% secure, so we cannot guarantee absolute security.',
        },
        {
          heading: '5. Your rights',
          body: 'You can request access, correction or deletion of your information at any time by writing to us on WhatsApp at +44 7311 754270.',
        },
        {
          heading: '6. Changes to this policy',
          body: 'We may update this policy occasionally. The most recent version will always be available on this page.',
        },
      ],
    },
    terms: {
      title: 'Terms of Service',
      sections: [
        {
          heading: '1. Service',
          body: 'AS Elite Contractor builds professional websites for local contractors. The service starts with a demo of your website for $25. If you approve the demo, you pay the remaining $225 ($250 total) and we publish the site. If you decide not to continue, the only charge is the $25 demo.',
        },
        {
          heading: '2. Payments',
          body: 'The $25 demo payment is non-refundable, as it covers the design work performed. The remaining $225 is charged only if you approve the demo and decide to launch your website. We accept payment by Zelle or card through Stripe. There are no mandatory monthly fees.',
        },
        {
          heading: '3. Domain',
          body: 'The domain is included for 1 year from the site publication date. After the first year, domain renewal is charged separately with an annual cost.',
        },
        {
          heading: '4. Delivery time',
          body: 'We deliver your website in under 72 hours from the moment we receive all the necessary information about your business (details, photos, services and contact).',
        },
        {
          heading: '5. Revisions',
          body: 'We include revisions until you are satisfied with the design. Final payment is not required until you approve the result.',
        },
        {
          heading: '6. Optional maintenance',
          body: 'We offer an optional maintenance plan of $30/month that includes photo updates, content changes and support. It is 100% optional and you can cancel it whenever you want.',
        },
        {
          heading: '7. Google profile management',
          body: 'Google Business profile management is a separate service at $30/month. The profile must be created by the client; we only manage and optimize it.',
        },
        {
          heading: '8. Client responsibilities',
          body: 'The client is responsible for providing accurate information and the necessary materials. The client owns the content they provide and warrants that they hold the rights to it.',
        },
        {
          heading: '9. Contact',
          body: 'For any questions about these terms, write to us on WhatsApp at +44 7311 754270.',
        },
      ],
    },
  },
  promo: {
    cta: 'I want mine →',
    closeLabel: 'Close notification',
    items: [
      {
        title: 'Carlos M. just ordered',
        message: 'His website will be ready in under 72 hours.',
      },
      {
        title: 'Limited spots this week',
        message: 'We only take a few projects so we can deliver fast.',
      },
      {
        title: 'Your demo for just $25',
        message: 'If you like it you pay the rest. If not, you only pay the demo.',
      },
      {
        title: 'More customers finding you',
        message: 'A professional website gives you instant credibility.',
      },
      {
        title: 'No contracts or monthly fees',
        message: 'One single payment. Your website is yours forever.',
      },
    ],
  },
  floating: {
    title: 'Ready for your website?',
    text: 'Message us and we reply today.',
    aria: 'Contact us on WhatsApp',
    closeLabel: 'Close message',
  },
}
